/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Button } from '@ziynx/components/buttons';
import Link from '@ziynx/components/links';
import Dropdown from '@ziynx/components/selects';
import Colors from '@ziynx/theme/colors';
import BannerNotification from '@ziynx/components/notifications';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Input from '@ziynx/components/inputs';
import { Form, FormRow } from '@ziynx/components/forms';
import {
  withAppManager, getMeasures, nameInputsRegex, emailRegex,
} from '@ziynx/components/utilities';
import List from '@ziynx/components/lists';
import { ContentContainer } from '@ziynx/components/layout';
import ActionTypes from '@ziynx/action-types';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import {
  ownerPermissions, adminPermissions, managerPermissions, ROLES,
} from './teamConstants';

type History = {
  push: (pathName: string, params?: { from: string, name: string }) => void,
  goBack: () => void
};

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, {email: string, password: string }) => void,
    goBack: () => void,
  },
|}>;

const PAGE_PADDING = {
  mobileSmall: '40px 16px',
  mobile: '40px 16px',
  default: '40px',
};

const LINK_PADDING = {
  mobileSmall: '32px',
  mobile: '32px',
  tablet: '32px',
  default: '48px',
};

const PageContentWrapper = styled.div`
  padding: ${({ viewport }) => getMeasures(PAGE_PADDING, viewport)};
  width: 100%;
  box-sizing: border-box;
  max-width: 688px;
  ${({ isMobile }) => isMobile && 'margin: auto;'}
`;

const StyledBannerWrapper = styled.div`
  position: fixed;
  z-index: 996;
  width: 100%;
  height: 0;
`;

const LinkWrapper = styled.div`
  padding-bottom: ${({ viewport }) => getMeasures(LINK_PADDING, viewport)};
`;

const ListWrapper = styled.div`padding-bottom: 40px;`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const _getPermissions = (currentRole) => {
  switch (currentRole) {
  case 'owner':
    return ownerPermissions;
  case 'admin':
    return adminPermissions;
  case 'manager':
    return managerPermissions;
  case 'default':
  default:
    return [];
  }
};

const InviteTeamMemberScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';
  const dispatch = useDispatch();
  const history: History = useHistory();
  const {
    control, errors, clearErrors, handleSubmit,
  } = useForm();

  const [currentRole, setCurrentRole] = useState('default');
  const [name, setName] = useState('');
  const [globalNotification, setGlobalNotification] = useState({
    visible: false,
    text: undefined,
    variant: 'error',
  });

  const onSubmit = async (data: {firstName: string, lastName: string, email: string, role: 'manager' | 'owner' | 'admin'}) => {
    const {
      firstName, lastName, email, role,
    } = data;
    setName(`${firstName} ${lastName}`);
    dispatch({
      type: ActionTypes.teamMember.post.START,
      body: {
        email: email?.toLowerCase().trim(),
        type: role,
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
      },
      id: 'me',
    });
  };

  const teamMemberService = useSelector(({ teamMember }) => teamMember?.member, shallowEqual);

  const PERMISSIONS = _getPermissions(currentRole);

  const BUTTON_WIDTH = getMeasures({
    mobileSmall: '100%',
    mobile: '100%',
    default: '264px',
  }, viewport);

  useEffect(() => () => {
    dispatch({ type: ActionTypes.teamMember.clear });
    setGlobalNotification({
      visible: false,
    });
  }, []);

  useEffect(() => {
    if (!teamMemberService?.fetching && teamMemberService?.success && !!errors) {
      dispatch({ type: ActionTypes.teamMember.clear });
      history.push('/team', { from: '/team/invite-member', name });
    }
    if (!teamMemberService?.fetching && !teamMemberService?.success && teamMemberService?.error) {
      setGlobalNotification({
        visible: true,
        text: 'User or invite already exists',
        variant: 'error',
      });
      setTimeout(() => {
        setGlobalNotification({
          visible: false,
        });
      }, 6000);
      dispatch({ type: ActionTypes.teamMember.clear });
    }
  }, [
    teamMemberService?.fetching,
    teamMemberService?.success,
    teamMemberService?.error,
    errors,
  ]);

  const _navigateToTeam = () => {
    history.goBack();
  };

  return (
    <>
      {globalNotification?.visible && (
        <StyledBannerWrapper>
          <BannerNotification
            animated
            showCloseIcon
            variant={globalNotification.variant}
            timeout={5000}
          >
            {globalNotification?.text}
          </BannerNotification>
        </StyledBannerWrapper>
      )}
      <Helmet>
        <title>Invite Team Member | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical>
        <PageContentWrapper viewport={viewport} isMobile={isMobile}>
          <LinkWrapper viewport={viewport}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link variant="caret" onClick={_navigateToTeam} color={Colors.GrayDark}>
              Back to team
            </Link>
          </LinkWrapper>
          <Form
            formID="invite-team-member"
            title="Invite team member"
            width="100%"
          >
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur,
                }) => {
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };

                  const onInputFocus = (): any => {
                    if (errors?.firstName) clearErrors('firstName');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.firstName}
                      errorLabel={errors?.firstName?.message}
                      inputName="firstName"
                      required
                      disabled={teamMemberService?.fetching}
                      label="First name"
                      onBlur={onBlur}
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                    />
                  );
                }}
                name="firstName"
                defaultValue=""
                rules={{
                  required: 'Please enter a first name',
                  pattern: {
                    value: nameInputsRegex,
                    message: 'Please enter a valid first name',
                  },
                }}
              />
            </FormRow>
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur,
                }) => {
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };
                  const onInputFocus = (): any => {
                    if (errors.lastName) clearErrors('lastName');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.lastName}
                      errorLabel={errors?.lastName?.message}
                      inputName="lastName"
                      required
                      disabled={teamMemberService?.fetching}
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                      onBlur={onBlur}
                      label="Last name"
                    />
                  );
                }}
                name="lastName"
                defaultValue=""
                rules={{
                  required: 'Please enter a last name',
                  pattern: {
                    value: nameInputsRegex,
                    message: 'Please enter a valid last name',
                  },
                }}
              />
            </FormRow>
            <FormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({
                  onFocus, onChange, onBlur,
                }) => {
                  /**
               * Function to clear any errors of inputs
               * when on focus event, if any
               */
                  const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                    (event?.target: HTMLInputElement);
                    onChange(event);
                  };

                  const onInputFocus = (): any => {
                    if (errors.email) clearErrors('email');
                    return onFocus;
                  };
                  return (
                    <Input
                      variant="text"
                      error={!!errors?.email}
                      errorLabel={errors?.email?.message}
                      inputName="email"
                      required
                      disabled={teamMemberService?.fetching}
                      label="Email"
                      onBlur={onBlur}
                      onChange={onInputChange}
                      onFocus={onInputFocus}
                    />
                  );
                }}
                name="email"
                defaultValue=""
                rules={{
                  required: 'Please enter an email',
                  pattern: {
                    value: emailRegex,
                    message: 'Please enter a valid email',
                  },
                }}
              />
            </FormRow>
            <FormRow>
              <Controller
                control={control}
                render={({ onChange }) => (
                  <Dropdown
                    inputName="role"
                    activeLabel="Role"
                    label="Role"
                    required
                    disabled={teamMemberService?.fetching}
                    options={ROLES}
                    defaultValue=""
                    onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      setCurrentRole(event);
                      onChange(event);
                    }}
                  />
                )}
                name="role"
                defaultValue=""
              />
            </FormRow>
          </Form>
          <ListWrapper>
            <List variant="icon" content={PERMISSIONS} />
          </ListWrapper>
          <StyledButton
            width={BUTTON_WIDTH}
            type="submit"
            form="invite-team-member"
            isLoading={teamMemberService?.fetching}
            disabled={teamMemberService?.fetching}
            onClick={handleSubmit(onSubmit)}
          >
            Invite
          </StyledButton>
        </PageContentWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(InviteTeamMemberScreen): React$ComponentType<
ManagedProps<Props>
>
);
